// FlixDetail.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format, fromUnixTime } from 'date-fns';
import { withTranslation } from 'react-i18next';
import withRouter from '../component/WithRouter.jsx';
import { SwiperSlide } from 'swiper/react';

import {
  SwiperWithDefaultStyle,
  SwiperAutoHeightStyle,
} from '../style/SwiperWithDefaultStyle.js';

import { formatTimeStringBySec } from '../resource/formatTimeString.js';
import trackMixpanel, {
  EventTypes,
  ButtonId,
  getViewId,
} from '../resource/mixpanel.js';
import { drmRulesetTestStatus as drmRulesetTestStatusConstant } from '../resource/drmConstants.js';
import { ProfileListType } from '../resource/profileConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import FlixPlayer from '../container/FlixPlayer.js';
import FlixDetailReaction from '../container/FlixDetailReaction.js';
import MessageMeta from '../container/MessageMeta.js';
import MessageThumbnail from '../container/MessageThumbnail.js';
import FlixDetailList from '../container/FlixDetailList.js';
import WithMessagePinData from '../container/WithMessagePinData.js';

import BackIcon from '../../img/ic_topbar_back.svg';
import MoreIcon from '../../img/ic_topbar_more.svg';
import ProIconSrc from '../../img/ic_pro_badge.svg';
import PinnedGreyIconSrc from '../../img/ic_pin_grey.svg';

import Tabs from '../component/Tabs.jsx';
import Tab from '../component/Tab.jsx';
import Loading from '../component/Loading.jsx';
import FlixDetailJsonLd from '../container/FlixDetailJsonLd.js';
import UserFeedItem from '../container/UserFeedItem.js';

import {
  textColor,
  color,
  flixPlayerZIndex,
  breakpoint,
  headerHeight,
} from '../style/variables.js';
import media from '../style/media.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import FlixBadge from '../container/FlixBadge.js';
import LinkSubstring from '../container/LinkSubstring.js';
import { MessageMoreFeature } from '../resource/messageConstants.js';
import getShareLink from '../resource/getShareLink.js';
import withFetchTranslation from '../resource/withFetchTranslation.js';
import getCanShare from '../resource/getCanShare.js';
import { PlayerContext } from '../resource/contexts.js';

const isServer = typeof window === 'undefined';
const canShare = getCanShare();

const FlixTab = {
  // PRO: 'pro',
  VIDEOS: 'videos',
  INFO: 'info',
  // FEED: 'feed',
};
const TabList = [FlixTab.VIDEOS, FlixTab.INFO];

/**
 * @typedef {Object} Props
 * @property {string} messageId
 * @property {string} senderId
 * @property {string} senderName
 * @property {boolean} canViewMedia
 * @property {string} title
 * @property {string} captionText
 * @property {string[]} hashtags
 * @property {number} postedAtUnix
 * @property {Error} detailError
 * @property {() => void} goBack
 * @property {({ id: string, httpProxyHeaders: object }) => Promise} fetchDetail
 * @property {(id: string) => Promise} fetchRating
 * @property {({ userId: string, httpProxyHeaders: object }) => Promise} fetchUser
 * @property {(userId: string) => Promise} followUser
 * @property {(userId: string) => Promise} unfollowUser
 * @property {(userId: string) => Promise} unblockUser
 * @property {(key: string) => string} t
 *
 * @typedef {Object} State
 * @property {boolean} shouldShowTranslation
 *
 * @extends {React.PureComponent<Props, State>}
 */
class FlixDetail extends React.PureComponent {
  state = {
    currentTab: FlixTab.VIDEOS,
    shouldShowTranslation: true,
    shouldHideAllTitle: true,
    isOverflowed: false,
    isClient: false,
    playerContext: {
      player: null,
      setPlayer: player => {
        this.setState({
          playerContext: {
            ...this.state.playerContext,
            player,
          },
        });
      },
    },
  };
  isInitToggleTitle = true;
  swiper = undefined;
  setPlayer = () => null;
  constructor(props) {
    super(props);
    // fetch data for SSR
    const {
      isFeedFirstPageFetched,
      staticContext,
      messageId,
      fetchDetail,
      fetchUser,
      fetchFeeds,
      senderId,
      senderName,
      castIds,
      firstCastUsername,
      detailError,
    } = props;
    if (staticContext.actions) {
      if (detailError) {
        staticContext.error = detailError;
      }
      if (messageId && !senderId) {
        staticContext.actions.push({
          function: fetchDetail,
          argumentObject: {
            id: messageId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
      if (messageId && !isFeedFirstPageFetched) {
        staticContext.actions.push({
          function: fetchFeeds,
          argumentObject: {
            type: `message-${messageId}`,
            page: 1,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
      if (senderId && !senderName) {
        staticContext.actions.push({
          function: fetchUser,
          argumentObject: {
            userId: senderId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
      if (castIds.length && !firstCastUsername) {
        staticContext.actions.push(
          ...castIds.map(castId => ({
            function: fetchUser,
            argumentObject: {
              userId: castId,
              httpProxyHeaders: staticContext.httpProxyHeaders,
            },
          }))
        );
      }
    }
  }
  updateBlockAndFolowingState = () => {
    const { fetchUserIsBlocked, fetchUserIsFollowing, senderId } = this.props;
    fetchUserIsBlocked({ userId: senderId });
    fetchUserIsFollowing({ userId: senderId });
  };
  componentDidMount() {
    const { fetchMe } = this.props;
    fetchMe();
    this.fetchData();
    this.updateBlockAndFolowingState();
    this.nextTick = setTimeout(() => {
      this.setState({ isClient: true });
    });
  }
  componentDidUpdate(prevProps) {
    const { messageId, isAuthed, senderId } = this.props;
    if (isAuthed !== prevProps.isAuthed) {
      this.fetchData();
      this.updateBlockAndFolowingState();
    }
    if (messageId !== prevProps.messageId) {
      this.fetchData();
      window.scrollTo(0, 0);
    }
    if (senderId !== prevProps.senderId) {
      this.updateBlockAndFolowingState();
    }
  }
  componentWillUnmount() {
    const { clearTopVideoPopupNotifications } = this.props;
    clearTimeout(this.updateAutoHeightTimeout);
    clearTimeout(this.nextTick);
    clearTopVideoPopupNotifications();
  }
  toggleTitle = () => {
    const { isOverflowed } = this.state;
    if (this.isInitToggleTitle && !isOverflowed) return;

    this.isInitToggleTitle = false;
    return this.setState(({ shouldHideAllTitle }) => ({
      shouldHideAllTitle: !shouldHideAllTitle,
    }));
  };
  renderHelmet = () => {
    const {
      messageId,
      title,
      captionText,
      ogMessageThumbnail,
      senderId,
      senderName,
      isPro,
      staticContext,
      t,
    } = this.props;
    const displayTitle = t('seo_cp_video_title', {
      flix_title: title,
      ns: TranslationNamespace.SEO,
    });
    const displayDescription = t('seo_cp_video_description', {
      cp_name: senderName,
      flix_title: title,
      flix_description: captionText,
      ns: TranslationNamespace.SEO,
    });
    const displayKeywords = t('seo_cp_video_keywords', {
      cp_name: senderName,
      flix_title: title,
      flix_description: captionText,
      ns: TranslationNamespace.SEO,
    });
    const ssrHostname = staticContext?.requestHeaders?.hostname || 'swag.live';
    const hostname = isServer ? ssrHostname : window.location.hostname;
    let serialCode = '';
    if (isPro) {
      // take out anything between "[]".
      serialCode = title.replace(/.*\[(.*)\].*/, '$1');
    }
    return (
      <>
        <Helmet>
          <title>{displayTitle}</title>
          <meta name="description" content={displayDescription} />
          <meta name="keywords" content={displayKeywords} />
          <meta
            property="og:url"
            content={`https://${hostname}/post/${messageId}`}
          />
          <meta property="og:title" content={displayTitle} />
          <meta property="og:description" content={displayDescription} />
          <meta property="og:image" content={ogMessageThumbnail} />
          <meta property="og:image:width" content="512" />
          <meta property="og:image:height" content="288" />
          <meta property="og:type" content="video.other" />
          <meta property="og:video:actor:id" content={senderId} />
          <meta property="og:video:actor:username" content={senderName} />
          <meta
            property="og:video:actor:url"
            content={`https://${hostname}/user/${senderId}/flix`}
          />
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:image" content={ogMessageThumbnail} />
          {serialCode && <meta property="og:video:tag" content={serialCode} />}
        </Helmet>
        <FlixDetailJsonLd messageId={messageId} />
      </>
    );
  };
  renderPinnedBadge = () => {
    const { messageId, t } = this.props;
    return (
      <WithMessagePinData
        messageId={messageId}
        profileListType={ProfileListType.FLIX}
      >
        {({ isPinned }) =>
          isPinned ? (
            <PinnedBadgeWrapper>
              <PinIcon src={PinnedGreyIconSrc} />
              <PinText>
                {t('pinned_post', {
                  ns: TranslationNamespace.POST,
                })}
              </PinText>
            </PinnedBadgeWrapper>
          ) : null
        }
      </WithMessagePinData>
    );
  };
  renderBadge = () => {
    const { badges, messageBadgeNotShowList } = this.props;
    const filteredBadges = badges.filter(
      value => !messageBadgeNotShowList.includes(value)
    );

    return filteredBadges?.length ? (
      <BadgeWrapper>
        {filteredBadges.map(value => (
          <FlixBadge key={value} badgeName={value} />
        ))}
      </BadgeWrapper>
    ) : null;
  };
  renderTabs = () => {
    const { t, castIds } = this.props;
    const { currentTab } = this.state;
    return (
      <TabWrapper>
        <Tabs
          onChange={(event, tab) => {
            if (tab === FlixTab.VIDEOS)
              this.trackUserClick(ButtonId.Detail.ButtonRelatedVideo);
            else if (tab === FlixTab.INFO)
              this.trackUserClick(ButtonId.Detail.ButtonInfo);

            this.setState({
              currentTab: tab,
            });

            this.swiper.slideTo(TabList.indexOf(tab));

            if (tab === FlixTab.INFO && castIds.length) {
              // swiper might get wrong height on changed moment, since cast list rendering
              this.updateAutoHeightTimeout = setTimeout(() => {
                this.swiper?.updateAutoHeight();
              }, 500);
            }
          }}
          value={currentTab}
          indicatorStyle={{ minWidth: '76px' }}
        >
          <Tab
            key={FlixTab.VIDEOS}
            value={FlixTab.VIDEOS}
            label={t('tab_related_video', {
              ns: TranslationNamespace.POST,
            })}
            labelStyle={{ textAlign: 'center', minWidth: '76px' }}
          />
          <Tab
            key={FlixTab.INFO}
            value={FlixTab.INFO}
            label={t('tab_info', {
              ns: TranslationNamespace.POST,
            })}
            labelStyle={{ textAlign: 'center', minWidth: '76px' }}
          />
        </Tabs>
      </TabWrapper>
    );
  };
  renderTabsContent = () => {
    return (
      <StyledSwiper
        autoHeight={true}
        slidesPerView={1}
        onSwiper={swiper => {
          this.swiper = swiper;
          window.swiper = swiper;
        }}
        onSlideChangeTransitionEnd={swiper => {
          this.setState({
            currentTab: TabList[swiper.realIndex],
          });
        }}
      >
        <SwiperSlide key={FlixTab.VIDEOS}>
          {this.renderFlixRelatedList()}
        </SwiperSlide>
        <SwiperSlide key={FlixTab.INFO}>
          {this.renderDetailInfomation()}
        </SwiperSlide>
      </StyledSwiper>
    );
  };
  renderTranslationButton = () => {
    const { t, hasTranslation } = this.props;
    return (
      hasTranslation && (
        <>
          <TranslationToggle
            role="button"
            onClick={() => {
              this.setState(prevState => ({
                shouldShowTranslation: !prevState.shouldShowTranslation,
              }));
            }}
          >
            {this.state.shouldShowTranslation
              ? t('show_original', {
                  ns: TranslationNamespace.POST,
                })
              : t('show_translate', {
                  ns: TranslationNamespace.POST,
                })}
          </TranslationToggle>
        </>
      )
    );
  };
  renderCastInfo = () => {
    const { t, castIds } = this.props;
    return (
      !!castIds.length && (
        <CastWrapper>
          <CastTitle>
            {t('cast', {
              ns: TranslationNamespace.POST,
            })}
          </CastTitle>
          <CastList>
            {castIds.map((id, index) => (
              <CastItem key={index}>
                <UserFeedItem id={id} size={64} withHalo marginTop={12} />
              </CastItem>
            ))}
          </CastList>
        </CastWrapper>
      )
    );
  };
  renderFlixRelatedList = () => {
    const { messageId } = this.props;
    return (
      <FlixDetailList
        listType="relatedMessages"
        messageId={messageId}
        shouldTrackRecommendEvent
        onChange={() => {
          if (this.swiper) {
            // re-calculate height if message list changed
            this.updateAutoHeightTimeout = setTimeout(() => {
              this.swiper?.updateAutoHeight();
            }, 0);
          }
        }}
      />
    );
  };
  renderMainInfomation = () => {
    const { shouldShowTranslation, shouldHideAllTitle, isOverflowed } =
      this.state;
    const { isPro, title, originalTitle, messageId, detailError } = this.props;
    // hide the reaction row if detail was not found
    const isNotFound = detailError && detailError.message === '404';
    return (
      <MainInfo>
        {this.renderPinnedBadge()}
        {this.renderBadge()}
        <Title
          ref={node => {
            this.setState({
              isOverflowed: node?.clientHeight !== node?.scrollHeight,
            });
          }}
          isOverflowed={isOverflowed}
          shouldHideAllTitle={shouldHideAllTitle}
          onClick={this.toggleTitle}
        >
          {isPro && (
            <ProIcon src={ProIconSrc} width="16" height="16" alt="pro icon" />
          )}
          <LinkSubstring>
            {shouldShowTranslation ? title : originalTitle}
          </LinkSubstring>
        </Title>
        <TranslationTogglerWrapper>
          {this.renderTranslationButton()}
        </TranslationTogglerWrapper>
        <MetaWrapper>
          <MessageMeta id={messageId} showLikesText />
          <MetaPartition>・</MetaPartition>
          <MessageMeta id={messageId} showViewCountText />
        </MetaWrapper>
        {!isNotFound && (
          <FlixDetailReactionWrapper>
            <FlixDetailReaction messageId={messageId} shouldShowUser />
          </FlixDetailReactionWrapper>
        )}
      </MainInfo>
    );
  };
  renderDetailInfomation = () => {
    const {
      t,
      captionText,
      originalCaptionText,
      hashtags,
      postedAtUnix,
      duration,
    } = this.props;
    const { shouldShowTranslation, isClient } = this.state;
    return (
      <div>
        {/* {this.renderCastInfo()} */}
        <MoreInfo>
          <TranslationTogglerWrapper>
            {this.renderTranslationButton()}
          </TranslationTogglerWrapper>
          {captionText && (
            <Description>
              <LinkSubstring>
                {shouldShowTranslation ? captionText : originalCaptionText}
              </LinkSubstring>
            </Description>
          )}
          {hashtags.length > 0 && (
            <Hashtags>{hashtags.map(this.renderTag)}</Hashtags>
          )}
          <Duration>
            {t('description_video_length', {
              ns: TranslationNamespace.POST,
            })}{' '}
            {duration ? formatTimeStringBySec(duration) : '-'}
          </Duration>
          <CreatedAt>
            {t('description_post_time', {
              ns: TranslationNamespace.POST,
            })}
            {/* render formated time on SSR will causing hydration fail due to timezone */}
            {isClient
              ? format(fromUnixTime(postedAtUnix), 'yyyy/MM/dd HH:mm')
              : '-'}
          </CreatedAt>
          <CopyrightWrapper>
            <Copyright>
              {t('hint_copyright', {
                ns: TranslationNamespace.POST,
              })}
            </Copyright>
            <Copyright>
              {t('broadcast_disclaimer_workoffiction', {
                ns: TranslationNamespace.POST,
              })}
            </Copyright>
            <Copyright>
              {t('hint_adult', {
                ns: TranslationNamespace.POST,
              })}
            </Copyright>
          </CopyrightWrapper>
        </MoreInfo>
      </div>
    );
  };
  render() {
    const {
      messageId,
      title,
      isAuthed,
      isVideoControlDisplayed,
      isPlayerFullscreen,
      assetIds,
      toggleModal,
      goBack,
      browserDrmRulesetTeststatus,
      shouldBlockTrailer,
    } = this.props;
    const { playerContext } = this.state;
    return (
      <PlayerContext.Provider value={playerContext}>
        <StyledPage>
          {this.renderHelmet()}
          <PlayerMainWrapper isPlayerFullscreen={isPlayerFullscreen}>
            <PlayerSize>
              <ThumbnailWrapper>
                <MessageThumbnail id={messageId} objectFit="cover" />
              </ThumbnailWrapper>
              <Player>
                {browserDrmRulesetTeststatus !==
                drmRulesetTestStatusConstant.TESTING ? (
                  <FlixPlayer
                    messageId={messageId}
                    assetId={assetIds[0]}
                    shouldUseSplash={true}
                    alt={title}
                    shouldShowControls={isAuthed}
                  />
                ) : (
                  <Loading />
                )}
              </Player>
              {/* TrailerBlocker will block control detector, so we need to keep
                header button on to ensure user can go back. */}
              {(isVideoControlDisplayed || shouldBlockTrailer) && (
                <BackButton
                  onClick={() => {
                    this.trackUserClick(ButtonId.All.ButtonBack);
                    return goBack();
                  }}
                >
                  <img src={BackIcon} alt="back" />
                </BackButton>
              )}
              {(isVideoControlDisplayed || shouldBlockTrailer) && (
                <MoreButton
                  onClick={() => {
                    this.trackUserClick(ButtonId.All.ButtonMore);
                    return toggleModal({
                      modalId: 'MessageMoreModal',
                      payload: {
                        messageId,
                        features: [
                          MessageMoreFeature.BLOCK,
                          MessageMoreFeature.FOLLOW,
                          MessageMoreFeature.PROFILE,
                          canShare
                            ? MessageMoreFeature.SHARE
                            : MessageMoreFeature.COPY,
                        ],
                        shareLink: getShareLink({
                          pathname: `/post/${messageId}`,
                        }),
                      },
                      isOpen: true,
                    });
                  }}
                >
                  <img src={MoreIcon} alt="more" />
                </MoreButton>
              )}
            </PlayerSize>
          </PlayerMainWrapper>
          <PlayerSize />

          <Container>
            <Info>
              {this.renderMainInfomation()}
              <HiddenOnMobile>{this.renderDetailInfomation()}</HiddenOnMobile>
            </Info>
            <TabContainer>
              {this.renderTabs()}
              {this.renderTabsContent()}
            </TabContainer>
            <HiddenOnMobile>
              <Aside>{this.renderFlixRelatedList()}</Aside>
            </HiddenOnMobile>
          </Container>
        </StyledPage>
      </PlayerContext.Provider>
    );
  }

  trackUserClick = buttonId => {
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        buttonId,
      },
    });
  };

  renderTag = (text, index) => {
    return (
      <HashTag key={index} to={encodeURI(`/hashtag/${text}/flix`)} title={text}>
        {text}
      </HashTag>
    );
  };

  fetchData() {
    const {
      messageId,
      canViewMedia,
      isAuthed,
      fetchDetail,
      fetchUser,
      fetchRating,
      fetchViewScope,
    } = this.props;

    fetchDetail({ id: messageId }).then(
      () => this.props.senderId && fetchUser({ userId: this.props.senderId })
    );

    if (isAuthed) {
      fetchRating(messageId);
      if (!canViewMedia) {
        fetchViewScope({ id: messageId });
      }
    }
  }
}

FlixDetail.propTypes = {
  staticContext: PropTypes.object,
  t: PropTypes.func.isRequired,
  messageId: PropTypes.string.isRequired,
  senderId: PropTypes.string,
  browserDrmRulesetTeststatus: PropTypes.string,
  senderName: PropTypes.string,
  firstCastUsername: PropTypes.string,
  canViewMedia: PropTypes.bool,
  hasTranslation: PropTypes.bool,
  isVideoControlDisplayed: PropTypes.bool,
  title: PropTypes.string,
  originalTitle: PropTypes.string,
  captionText: PropTypes.string,
  originalCaptionText: PropTypes.string,
  ogMessageThumbnail: PropTypes.string,
  hashtags: PropTypes.arrayOf(PropTypes.string),
  postedAtUnix: PropTypes.number,
  detailError: PropTypes.instanceOf(Error),
  isAuthed: PropTypes.bool,
  isPro: PropTypes.bool,
  isPlayerFullscreen: PropTypes.bool,
  goBack: PropTypes.func.isRequired,
  push: PropTypes.func.isRequired,
  fetchViewScope: PropTypes.func,
  fetchDetail: PropTypes.func.isRequired,
  fetchRating: PropTypes.func,
  fetchUser: PropTypes.func,
  fetchMe: PropTypes.func.isRequired,
  duration: PropTypes.number,
  isFeedFirstPageFetched: PropTypes.bool,
  fetchFeeds: PropTypes.func,
  fetchUserIsBlocked: PropTypes.func,
  fetchUserIsFollowing: PropTypes.func,
  messageBadgeNotShowList: PropTypes.array,
  badges: PropTypes.array,
  clearTopVideoPopupNotifications: PropTypes.func,
  castIds: PropTypes.array,
  assetIds: PropTypes.array,
  shouldBlockTrailer: PropTypes.bool,
  toggleModal: PropTypes.func,
};

FlixDetail.defaultProps = {
  staticContext: {},
  title: 'no title',
  originalTitle: '',
  senderId: '',
  browserDrmRulesetTeststatus: '',
  senderName: '',
  firstCastUsername: '',
  captionText: '',
  originalCaptionText: '',
  ogMessageThumbnail: '',
  hashtags: [],
  postedAtUnix: 0,
  detailError: null,
  isAuthed: false,
  isPro: false,
  isPlayerFullscreen: false,
  hasTranslation: false,
  isVideoControlDisplayed: false,
  isFeedFirstPageFetched: false,
  fetchFeeds: () => null,
  fetchViewScope: () => null,
  fetchRating: () => null,
  fetchUser: () => null,
  previousRoute: '',
  duration: 0,
  fetchUserIsBlocked: () => null,
  fetchUserIsFollowing: () => null,
  messageBadgeNotShowList: [],
  badges: [],
  clearTopVideoPopupNotifications: () => null,
  castIds: [],
  assetIds: [],
  shouldBlockTrailer: true,
  toggleModal: () => null,
};

const StyledPage = styled.div`
  padding-top: ${headerHeight}px;
  min-height: 100vh;
  color: ${textColor.white};
`;

const Container = styled.div`
  padding-bottom: 100px;
  max-width: 1200px;
  background-color: #232323;
  margin: 0 auto;

  display: flex;
  flex-direction: row;
  @media (max-width: ${breakpoint.mobile}px) {
    display: block;
  }
`;

const TabContainer = styled.div`
  display: none;
  @media (max-width: ${breakpoint.mobile}px) {
    display: block;
  }
`;

const HiddenOnMobile = styled.div`
  @media (max-width: ${breakpoint.mobile}px) {
    display: none;
  }
`;

const Aside = styled.div`
  position: relative;
  max-width: 388px;
`;

const Info = styled.div`
  flex: 1;
  background-color: ${color.grey['900']};

  padding: 40px 20px 20px 20px;
  @media (max-width: ${breakpoint.mobile}px) {
    padding: 16px 16px 0 16px;
  }
`;

const MainInfo = styled.header``;

const PlayerMainWrapper = styled.main`
  position: absolute;
  left: 0;
  right: 0;
  z-index: ${({ isPlayerFullscreen }) => (isPlayerFullscreen ? 9 : 4)};
  width: 100%;
  background-color: #000;
  video {
    width: inherit;
    height: inherit;
  }

  ${media.mobile`
    position: fixed;
  `};
`;

const Player = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const PlayerSize = styled.div`
  position: relative;
  padding-bottom: 60vh;
  width: 100%;

  ${media.mobile`
    padding-bottom: 56.25%;
`};
`;

const ThumbnailWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const HeaderButton = styled.button`
  ${ResetButtonStyle};
  position: absolute;
  top: 12px;
  left: 16px;
  z-index: ${flixPlayerZIndex.backButton};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;

  & > img {
    display: block;
    width: 28px;
    height: 28px;
    object-fit: contain;
  }

  @media (hover: hover) {
    :hover {
      background-color: rgba(100, 100, 100, 0.2);
      opacity: 1;
    }
  }

  ${media.mobile`
    display: none;
  `};
`;

const BackButton = styled(HeaderButton)``;

const MoreButton = styled(HeaderButton)`
  left: auto;
  right: 16px;
`;

const BadgeWrapper = styled.div`
  margin-bottom: 12px;
`;

const PinnedBadgeWrapper = styled.div`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const PinIcon = styled.img`
  margin-right: 4px;
  width: 18px;
  height: 18px;
  object-fit: contain;
  ${media.mobile`
    width: 16px;
    height: 16px;
  `};
`;

const PinText = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: ${color.neutral[55]};
  ${media.mobile`
    font-size: 12px;
  `};
`;

const Title = styled.h1`
  display: block;
  margin: 0px;
  margin-bottom: 8px;
  margin-top: -4px;
  width: 100%;
  font-size: 22px;
  @media (max-width: ${breakpoint.mobile}px) {
    margin-bottom: 8px;
    font-size: 16px;
  }
  font-weight: 600;
  line-height: 1.5;
  overflow: hidden;
  cursor: ${({ isOverflowed }) => (isOverflowed ? 'pointer' : 'auto')};
  ${({ shouldHideAllTitle }) =>
    shouldHideAllTitle
      ? css`
          display: -webkit-box;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        `
      : null}
`;

const ProIcon = styled.img`
  margin-right: 8px;
  display: inline-block;
  vertical-align: middle;
`;

const TranslationTogglerWrapper = styled.div`
  margin-bottom: 10px;
  :empty {
    margin-bottom: 0px;
  }
`;

const MetaWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-top: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(100, 100, 100, 0.15);
  color: ${textColor.grey};
  font-size: 14px;
  @media (max-width: ${breakpoint.mobile}px) {
    border-bottom: none;
    font-size: 12px;
  }
`;

const MetaPartition = styled.div`
  width: 12px;
  height: 12px;
  margin: 0 4px;
  font-size: 14px;
  color: ${textColor.black};
`;

const TranslationToggle = styled.div`
  display: inline-block;
  cursor: pointer;
  font-size: 14px;
  @media (max-width: ${breakpoint.mobile}px) {
    /* margin-bottom: 10px; */
    font-size: 12px;
  }
  font-weight: 600;
  color: ${textColor.black};
`;

const Hashtags = styled.nav`
  margin-bottom: 16px;
  @media (max-width: ${breakpoint.mobile}px) {
    margin-bottom: 8px;
  }
  display: flex;
  flex-wrap: wrap;
`;

const HashTag = styled(Link)`
  margin-right: 4px;
  margin-bottom: 4px;
  border-radius: 10px;
  padding: 4px;
  background-color: rgba(255, 255, 255, 0.2);
  color: ${textColor.white};
  font-size: inherit;
  line-height: 1;

  &::before {
    content: '#';
  }
`;

const FlixDetailReactionWrapper = styled.div`
  width: 100%;
  padding: 16px 0;
  background-color: ${color.grey['900']};
  border-bottom: 1px solid rgba(100, 100, 100, 0.15);
  @media (max-width: ${breakpoint.mobile}px) {
    border-bottom: none;
  }
`;

const MoreInfo = styled.div`
  background-color: #232323;
  padding: 20px 0;
  font-size: 14px;
  @media (max-width: ${breakpoint.mobile}px) {
    padding: 20px 16px;
    font-size: 12px;
  }
`;

const Description = styled.div`
  margin-bottom: 16px;
  color: ${textColor.grey};
  font-size: 16px;
  @media (max-width: ${breakpoint.mobile}px) {
    font-size: 14px;
  }
  line-height: 1.5;
  white-space: pre-wrap;
`;

const Duration = styled.div`
  margin-bottom: 16px;
  color: ${textColor.black};
  font-size: inherit;
  line-height: 1;
`;

const CreatedAt = styled.div`
  margin-bottom: 16px;
  color: ${textColor.black};
  font-size: inherit;
  line-height: 1;
`;

const CopyrightWrapper = styled.div`
  flex: auto;
  display: flex;
  flex-wrap: wrap;
`;

const Copyright = styled.div`
  margin-top: 8px;
  margin-right: 8px;
  padding: 2px 4px;
  max-width: 100%;
  flex: none;
  display: inline-flex;
  font-size: inherit;
  opacity: 0.5;
  color: #fff;
  background-color: rgba(100, 100, 100, 0.5);
`;

const TabWrapper = styled.div`
  background-color: #232323;
  border-bottom: 1px solid rgba(100, 100, 100, 0.15);
`;

const CastWrapper = styled.div`
  padding: 20px 0;
  background-color: #232323;
  border-bottom: 1px solid rgba(100, 100, 100, 0.5);
  @media (max-width: ${breakpoint.mobile}px) {
    padding: 20px 16px;
  }
`;

const CastTitle = styled.div`
  padding-bottom: 16px;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
`;

const CastList = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0 8px;

  overflow-y: hidden;
  overflow-x: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const CastItem = styled.div`
  position: relative;
  display: flex;
  margin-right: 20px;
  padding-top: 6px;
`;

const StyledSwiper = styled(SwiperWithDefaultStyle)`
  ${SwiperAutoHeightStyle};
`;

export default withTranslation()(withRouter(withFetchTranslation(FlixDetail)));
